import type { InvestmentOwner } from '@/api/fund/types';
import type {
  TransactionViewDirection,
  TransactionViewOrderBy,
  TransactionViewsParams,
} from '@/api/transaction-views/queries';
import type {
  TransactionListFilters,
  TransactionSortField,
} from '@/features/transactions/utils/transaction-list';

type TransactionFilterState = Pick<
  TransactionListFilters,
  'searchTerm' | 'dateRange' | 'direction' | 'denomination' | 'type' | 'sortField' | 'sortDirection'
>;

const ORDER_FIELDS: Record<TransactionSortField, TransactionViewOrderBy> = {
  direction: 'direction',
  fund: 'counterparty',
  date: 'date',
  amount: 'amount',
  type: 'type',
  notes: 'date',
};

export const buildTransactionQueryParams = (
  filters: TransactionFilterState,
  owner: InvestmentOwner,
): Omit<TransactionViewsParams, 'page' | 'limit'> => {
  const [dateFrom, dateTo] = normalizeDateRange(filters.dateRange);
  const search = filters.searchTerm.trim();

  return {
    ...owner,
    ...(search ? { search } : {}),
    ...(dateFrom ? { dateFrom: startOfDay(dateFrom).toISOString() } : {}),
    ...(dateTo ? { dateTo: endOfDay(dateTo).toISOString() } : {}),
    ...(filters.direction ? { direction: filters.direction as TransactionViewDirection } : {}),
    ...(filters.denomination ? { denomination: filters.denomination } : {}),
    ...(filters.type ? { type: filters.type } : {}),
    orderBy: ORDER_FIELDS[filters.sortField],
    orderDirection: filters.sortDirection === 'asc' ? 'ASC' : 'DESC',
  };
};

const normalizeDateRange = (
  range: readonly [Date | null, Date | null],
): readonly [Date | null, Date | null] => {
  const from = validDate(range[0]);
  const to = validDate(range[1]);
  if (from !== null && to !== null && from.getTime() > to.getTime()) return [to, from];
  return [from, to];
};

const validDate = (value: Date | null): Date | null =>
  value !== null && Number.isFinite(value.getTime()) ? value : null;

const startOfDay = (value: Date): Date => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfDay = (value: Date): Date => {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
};
